import React, { useRef, useState } from "react";
import { Avatar, Card } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { htmlDecode } from "../../utils/convertHtml";
import { useSnackbar } from "notistack";
import {
  csvImage,
  docImage,
  pdfImage,
  wordImage,
  audioImage,
  xlsxImage,
  otherImage,
} from "../../assets";
import { timeAgo } from "../../utils/constant";
import { ModelBox } from "../../components";
import { picBaseUrl } from "../../config/config";
import CustomPopoverSection from "../../components/menuOption/CustomPopoverSection";
import Editor from "../../components/editor/Editor";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import {
  _Add_Support_Ticket_Comment,
  _Delete_Support_Ticket_Comment,
  _Update_Support_Ticket_Comment,
} from "../../DAL/Listing/SupportTickets";
import {
  Delete_image,
  Upload_File_n_S3,
} from "../../DAL/FileUpload/s3FileUpload";
import FileViewerModal from "./DocumentModal";
import { useAdminContext } from "../../Hooks/AdminContext";

function ChatCard({
  comments,
  ticket_id,
  list_data,
  supportTicketData,
  isLoading,
  setIsLoading,
}) {
  const [open, setOpen] = useState(false);
  const myDivRef = useRef(null);
  const [imageUrl, setImageUrl] = useState("");
  const [files, setFiles] = useState([]);
  const [oldImagesArray, setOldImagesArray] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [commentId, setCommentId] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [shortDescriptionCk, setShortDescriptionCk] = useState("");
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);
  const [showButtons, setShowButtons] = React.useState({
    addButton: true,
    editButton: false,
  });
  const { socket } = useAdminContext();

  /**
   * handleOpen - Opens an image viewer modal with the provided image path.
   *
   * This function:
   * - Accepts an `image_path` parameter, which is the URL or path of the image to display.
   * - Sets the `imageUrl` state to the provided `image_path`.
   * - Sets the state `open` to `true` to open the image viewer modal.
   */
  const handleOpen = (image_path) => {
    setImageUrl(image_path);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  /**
   * handleRemove - Removes a file from the list of files.
   *
   * This function:
   * - Accepts an `index` parameter, which specifies the position of the file to remove in the `files` array.
   * - Removes the file at the given index using the `splice` method.
   * - Updates the `files` state by spreading the remaining files into a new array to trigger re-rendering.
   */
  const handleRemove = (index) => {
    files.splice(index, 1);
    setFiles([...files]);
  };

  /**
   * handleUpload - Handles the file upload process, including validation and state update.
   *
   * This function:
   * - Limits the number of files to 3 and displays an error message if more files are selected.
   * - Validates that each file is no larger than 20MB and displays an error message if any file exceeds the limit.
   * - For each valid file, creates an object containing its details (path, type, and extension).
   * - Adds the valid files to the `files` state to trigger a re-render and allow further processing or display.
   */
  const handleUpload = (event) => {
    let setImageObject = {};
    const fileList = event.target.files;
    const arr = [];
    let totalSize = 0; // To track total file size

    // Check if the user selects more than 3 files
    if (fileList.length > 3) {
      enqueueSnackbar("You can only upload up to 3 files at a time.", {
        variant: "error",
      });
      return;
    }

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];

      // Check file size (limit: 20MB per file)
      if (file.size > 20 * 1024 * 1024) {
        enqueueSnackbar(`"${file.name}" exceeds the 20MB limit.`, {
          variant: "error",
        });
        return;
      }

      setImageObject = {
        path: file,
        type: "file",
        extension: file.type,
      };

      arr.push(setImageObject);
    }

    setFiles((prevFiles) => [...prevFiles, ...arr]);
  };

  //Deleting Note
  const handleDelete = async () => {
    setDeleteLoading(true);
    const result = await _Delete_Support_Ticket_Comment(commentId);
    if (result.code === 200) {
      socket.emit("support_ticket_event", {
        notification: result.notification,
      });
      list_data();
      setDeleteLoading(false);
      enqueueSnackbar(result.message, { variant: "success" });
      setOpenDelete(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setDeleteLoading(false);
    }
  };

  //Getting Note In textarea
  const handleEdit = (comment) => {
    if (myDivRef.current) {
      myDivRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    setShortDescriptionCk(comment.message);
    setCommentId(comment._id);
    setShowButtons({
      addButton: false,
      editButton: true,
    });
    let setImageObject = {};
    comment.comment_image?.map((images, index) => {
      setImageObject = {
        thumbnail_1: images,
        thumbnail_2: images,
        type: "image",
      };
      files.push(setImageObject);
      oldImagesArray.push(setImageObject);
    });
  };

  /**
   * handleCancelUpdate - Resets the form to its initial state when the update is canceled.
   *
   * This function:
   * - Clears the short description and comment ID.
   * - Resets the button states to show the "add" button and hide the "edit" button.
   * - Clears any selected files.
   */
  const handleCancelUpdate = () => {
    setShortDescriptionCk("");
    setCommentId("");
    setShowButtons({
      addButton: true,
      editButton: false,
    });
    setFiles([]);
  };

  /**
   * handleAgreeDelete - Prepares the comment for deletion by setting the comment ID and opening the delete confirmation modal.
   *
   * This function:
   * - Sets the `commentId` to the ID of the comment to be deleted.
   * - Opens the delete confirmation modal.
   */
  const handleAgreeDelete = (comment) => {
    setCommentId(comment._id);
    setOpenDelete(true);
  };

  /**
   * MENU_OPTIONS - An array of menu options for a context menu or dropdown.
   *
   * This array contains objects representing different menu actions:
   * - "Edit" action: Triggers the `handleEdit` function.
   * - "Delete" action: Triggers the `handleAgreeDelete` function.
   * Each option includes a label, an icon, and a function to handle the respective action.
   */
  const MENU_OPTIONS = [
    {
      label: "Edit",
      icon: "akar-icons:edit",
      handleClick: handleEdit,
    },

    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: handleAgreeDelete,
    },
  ];

  /**
   * UploadImages - Uploads images to an S3 bucket and returns the file path.
   *
   * This function takes `formData`, which contains the file data, and calls the `Upload_File_n_S3` function to upload the file.
   * Upon successful upload, it returns the file's path.
   */
  const UploadImages = async (formData) => {
    const result = await Upload_File_n_S3(formData);
    return result.path;
  };

  /**
   * handleSubmit - Handles the submission of the support ticket comment with file uploads.
   *
   * This function is triggered when the user submits the form. It performs the following checks:
   * - If more than 3 files are selected, an error snackbar is shown.
   * - If no files or comment is provided, an error snackbar prompts the user to input data.
   *
   * If valid, it uploads the selected files to an S3 bucket using `UploadImages`. The image paths are then included in the form data.
   * The support ticket comment is sent to the backend for processing, and upon success:
   * - The socket emits a notification for a new comment,
   * - The list of comments is updated,
   * - The form is reset,
   * - A success snackbar is shown.
   *
   * If the submission fails, an error snackbar is shown and the loading state is cleared.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (files.length > 3) {
      enqueueSnackbar("You can only upload up to 3 files at a time.", {
        variant: "error",
      });
      return;
    }

    setIsLoading(true);

    if ((!files || files.length === 0) && !shortDescriptionCk?.trim()) {
      enqueueSnackbar(
        "Please enter a comment or select at least one file to send the message.",
        { variant: "error" }
      );
      setIsLoading(false);
      return;
    }

    const results = files.map((image, index) => {
      const formData = new FormData();
      formData.append("image", image.path);
      const result = UploadImages(formData);
      return result;
    });
    // console.log(results);

    Promise.all(results).then(async (img_results) => {
      var setImageArray = [];
      img_results.map((image_path, index) => {
        setImageArray.push(image_path);
      });
      const formDataObject = {
        support_ticket_id: ticket_id,
        message: shortDescriptionCk,
      };
      if (files.length > 0) {
        formDataObject.comment_image = setImageArray;
      }
      setButtonLoading(true);
      const result = await _Add_Support_Ticket_Comment(
        JSON.stringify(formDataObject)
      );
      if (result.code === 200) {
        socket.emit("support_ticket_event", {
          notification: result.notification,
        });
        list_data();
        setShortDescriptionCk("");
        setFiles([]);
        enqueueSnackbar(result.message, { variant: "success" });
        setShowButtons(false);
        setIsLoading(false);
      } else {
        setShowButtons(false);
        setIsLoading(false);
        enqueueSnackbar(result.message, { variant: "error" });
      }
    });
  };

  /**
   * DeleteImages - Deletes the specified images from the server.
   *
   * This function checks if any images are provided in the `formData`. If no images are provided,
   * it logs an error message. Otherwise, it maps through the images and creates a `requestData` object
   * that contains the paths of the images to be deleted.
   *
   * The function then sends a request to delete the images using the `Delete_image` function.
   * The result of the deletion operation is returned.
   */
  const DeleteImages = async (formData) => {
    const images = formData?.moment_image;
    if (!images || images.length === 0) {
      console.error("No images provided for deletion.");
      return;
    }

    const requestData = {
      path: images.map((images) => images.thumbnail_1),
    };
    const result = await Delete_image(requestData);
    return result;
  };

  /**
   * handleUpdate - Handles the update process of a support ticket comment, including file uploads and deletions.
   *
   * This function validates the files selected for upload, ensuring that no more than 3 files are selected and
   * that either a comment or at least one file is provided. It then checks for any images that need to be deleted
   * by comparing the old images with the newly selected ones. If any images have been removed, they are passed
   * to the `DeleteImages` function for deletion.
   *
   * It then uploads the newly selected images and constructs the form data, which includes the comment message
   * and the images to be uploaded. The images are uploaded and, once complete, a request to update the comment
   * is sent to the server. If successful, the UI is updated with the new comment details, and a success message
   * is displayed. If an error occurs, an error message is shown.
   */
  const handleUpdate = async (e) => {
    e.preventDefault();

    if (files.length > 3) {
      enqueueSnackbar("You can only upload up to 3 files at a time.", {
        variant: "error",
      });
      return;
    }

    if ((!files || files.length === 0) && !shortDescriptionCk?.trim()) {
      enqueueSnackbar(
        "Please enter a comment or select at least one file to send the message.",
        { variant: "error" }
      );
      setIsLoading(false);
      return;
    }
    const deleted_images_array = [];
    let deleted_images_object = {};
    oldImagesArray.map((old_image, index) => {
      const filtered_array = files.filter(
        (image, index) => image.thumbnail_1 == old_image.thumbnail_1
      );
      delete old_image.type;
      if (filtered_array.length == 0) {
        deleted_images_array.push(old_image.thumbnail_1);
      }
    });
    if (deleted_images_array.length > 0) {
      deleted_images_object = {
        comment_image: deleted_images_array,
      };
      const result = DeleteImages(deleted_images_object);
    }

    const results = files.map((image, index) => {
      if (image.type === "file") {
        const formData = new FormData();
        formData.append("image", image.path);
        const result = UploadImages(formData);
        return result;
      } else {
        return image;
      }
    });

    Promise.all(results).then(async (img_results) => {
      var setImageArray = [];
      img_results.map((image_path, index) => {
        setImageArray.push(
          image_path.thumbnail_1 ? image_path.thumbnail_1 : image_path
        );
      });
      const formDataObject = {
        message: shortDescriptionCk,
      };
      if (files.length > 0) {
        formDataObject.comment_image = setImageArray;
      }

      // console.log(
      //   formDataObject,
      //   "formDataObjectformDataObjectformDataObjectformDataObject"
      // );

      setButtonLoading(true);
      const result = await _Update_Support_Ticket_Comment(
        formDataObject,
        commentId
      );
      if (result.code === 200) {
        // console.log(result, "result resultresultresultresultresult");
        setShortDescriptionCk("");
        setFiles([]);
        list_data();
        setCommentId(result?.support_ticket_comment._id);
        setShowButtons({
          addButton: true,
          editButton: false,
        });
        setShowButtons(false);
        enqueueSnackbar(result.message, { variant: "success" });
      } else {
        setShowButtons(false);
        enqueueSnackbar(result.message, { variant: "error" });
      }
    });
  };

  const imageLinks = {
    docx: wordImage,
    mp3: audioImage,
    pdf: pdfImage,
    csv: csvImage,
    doc: docImage,
    xlsx: xlsxImage,
    xls: xlsxImage,
    other: otherImage,
  };

  /**
   * getCommentRecourse - Determines the appropriate URL for a comment image based on its file extension.
   *
   * This function checks the file extension of the given file and returns the correct image URL:
   * - If the file is an image (e.g., JPG, PNG, JPEG, WebP), it returns the thumbnail URL.
   * - If the extension is known but not an image, it returns a specific image link based on the extension.
   * - If the extension is not recognized, it returns a default image link.
   *
   * This function ensures that images are displayed correctly based on their format or provides a fallback when
   * an unsupported format is encountered.
   */
  const getCommentRecourse = (file) => {
    const ext = file?.split(".").pop();
    if (
      ext == "jpg" ||
      ext == "JPG" ||
      ext == "png" ||
      ext == "webp" ||
      ext == "jpeg" ||
      ext == "JPEG" ||
      ext == "PNG"
    ) {
      return picBaseUrl + file.thumbnail_1;
    } else if (imageLinks[ext]) {
      return imageLinks[ext];
    } else {
      return imageLinks.other;
    }
  };

  /**
   * getCommentImage - Retrieves the appropriate image URL based on the file type and extension.
   *
   * This function handles two types of file objects:
   * 1. **File object**: If the file is a local file (type "file"), the function checks the file extension (e.g., JPG, PNG, JPEG) and returns a URL using `URL.createObjectURL()` to display the image.
   * 2. **Image object**: If the file is not a local file (i.e., it contains a `thumbnail_1` property), the function checks the file extension and returns the appropriate image URL either from a base URL or from a predefined image link based on the extension.
   *
   * If the extension is not recognized, a default image URL is returned.
   */
  const getCommentImage = (file) => {
    if (file.type === "file") {
      const ext = file?.path.name?.split(".").pop();
      if (
        ext == "jpg" ||
        ext == "JPG" ||
        ext == "png" ||
        ext == "webp" ||
        ext == "jpeg" ||
        ext == "JPEG" ||
        ext == "PNG"
      ) {
        return URL.createObjectURL(file.path);
      } else if (imageLinks[ext]) {
        return imageLinks[ext];
      } else {
        return imageLinks.other;
      }
    } else {
      const ext = file?.thumbnail_1?.split(".").pop();
      if (
        ext == "jpg" ||
        ext == "JPG" ||
        ext == "png" ||
        ext == "webp" ||
        ext == "jpeg" ||
        ext == "JPEG" ||
        ext == "PNG"
      ) {
        return picBaseUrl + file.thumbnail_1;
      } else if (imageLinks[ext]) {
        return imageLinks[ext];
      } else {
        return imageLinks.other;
      }
    }
  };

  return (
    <div className="col-12">
      <Card className="chat-main-card p-2 mt-3">
        <div className="chat-message-box">
          {comments &&
            comments.map((comment, i) => {
              const comment_date = timeAgo(comment.action_date);
              return (
                <>
                  <div className="incoming-message w-100 ps-2 pe-3">
                    <div className="chat-message-body pt-1 pb-4 mt-2">
                      <div className="d-flex ">
                        <div className="circle-image circle-image-chat">
                          {comment?.action_by?.profile_image ? (
                            <img
                              src={
                                picBaseUrl + comment?.action_by?.profile_image
                              }
                            />
                          ) : (
                            <Avatar
                              alt={comment?.action_by?.action_name}
                              src={
                                picBaseUrl + comment?.action_by?.profile_image
                              }
                              sx={{ marginRight: 1, marginLeft: 1 }}
                            />
                          )}
                        </div>
                        <div className="description w-100 custom-popover-box position-relative">
                          <div className="set-title-width d-flex justify-content-between w-100 pe-3">
                            <p className="card-title pt-2 mb-0">
                              {htmlDecode(
                                comment?.action_by?.first_name +
                                  " " +
                                  comment?.action_by?.last_name
                              )}
                            </p>
                            <div className="d-flex">
                              <p className="date-color me-2 mb-0">
                                {comment_date}
                              </p>
                              {comment?.action_by.type === 0 ? (
                                supportTicketData?.ticket_status === 0 ? (
                                  <CustomPopoverSection
                                    menu={MENU_OPTIONS}
                                    data={comment}
                                  />
                                ) : (
                                  ""
                                )
                              ) : (
                                ""
                              )}
                            </div>
                          </div>
                          <div
                            style={{
                              overflowWrap: "anywhere",
                            }}
                            className="mt-3"
                            dangerouslySetInnerHTML={{
                              __html: comment.message,
                            }}
                          ></div>

                          {comment.comment_image &&
                            comment.comment_image.map((image, i) => {
                              const ext = image.split(".").pop();
                              if (
                                ext == "jpg" ||
                                ext == "JPG" ||
                                ext == "png" ||
                                ext == "webp" ||
                                ext == "jpeg" ||
                                ext == "JPEG" ||
                                ext == "PNG" ||
                                ext == "SVG"
                              ) {
                                return (
                                  <span className="preview" key={i}>
                                    <img
                                      onClick={() => {
                                        handleOpen(picBaseUrl + image);
                                      }}
                                      className="p-0"
                                      src={picBaseUrl + image}
                                    />
                                  </span>
                                );
                              } else {
                                return (
                                  <span className="preview" key={i}>
                                    <a
                                      rel="noopener noreferrer"
                                      className="pointer"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setSelectedFileUrl(picBaseUrl + image);
                                      }}
                                    >
                                      <img src={getCommentRecourse(image)} />
                                    </a>
                                  </span>
                                );
                              }
                            })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr />
                </>
              );
            })}
          <form onSubmit={showButtons.addButton ? handleSubmit : handleUpdate}>
            <div className="col-12" ref={myDivRef}>
              <h5>Enter Message* </h5>
              <Editor
                placeholder="Description"
                onChange={(e) => {
                  setShortDescriptionCk(e);
                }}
                value={shortDescriptionCk}
              />
            </div>
            <div className="w-100 px-3 d-flex">
              <div className="row w-100 mb-3 add-photo">
                <p className="mt-3 mb-0">Recommended Size (1000x670)</p>
                {files &&
                  files.map((file, index) => (
                    <div
                      className="col-sm-3 col-md-2 col-lg-1 mt-2 ms-2 p-0"
                      key={index}
                    >
                      <span className="preview">
                        <span onClick={() => handleRemove(index)}>x</span>
                        <img src={getCommentImage(file)} />
                      </span>
                    </div>
                  ))}
                <div className="col-sm-3 col-md-2 col-lg-1 mt-2">
                  <span className="upload-button">
                    <input
                      color="primary"
                      type="file"
                      id="icon-button-file"
                      style={{ display: "none" }}
                      onChange={handleUpload}
                      multiple={true}
                      accept="image/*,.pdf,.xlsx,.xls,.docx,.csv,.doc"
                    />
                    <label htmlFor="icon-button-file">
                      <CloudUploadIcon
                        color="#fff"
                        sx={{
                          fill: "#fff",
                        }}
                      />
                    </label>
                  </span>
                </div>
              </div>
            </div>
            {showButtons.addButton === true &&
              (supportTicketData.ticket_status == 0 ? (
                <button
                  type="submit"
                  className="mt-2 float-end small-contained-button mb-4"
                  disabled={buttonLoading}
                >
                  {buttonLoading ? "Saving..." : "Send"}
                </button>
              ) : (
                <button
                  type="submit"
                  className="mt-2 float-end small-contained-button disabled mb-4"
                  disabled={true}
                >
                  Send
                </button>
              ))}
            {showButtons.editButton === true && (
              <div className="d-flex justify-content-end mb-4 me-4">
                <button
                  className="mt-2 me-2 small-contained-button"
                  onClick={handleCancelUpdate}
                >
                  Cancel
                </button>
                <button
                  className="mt-2 small-contained-button"
                  type="submit"
                  disabled={buttonLoading}
                >
                  {buttonLoading ? "Updating..." : "Update"}
                </button>
              </div>
            )}
            <DeleteConfirmation
              open={openDelete}
              handleClose={() => setOpenDelete(false)}
              setOpen={setOpenDelete}
              isLoading={deleteLoading}
              title={"Are you sure you want to delete this comment?"}
              handleAgree={handleDelete}
              Btn_Title={"Delete"}
            />
          </form>
        </div>
      </Card>
      <ModelBox open={open} handleClose={handleClose} image_url={imageUrl} />
      <FileViewerModal
        open={Boolean(selectedFileUrl)}
        onClose={() => setSelectedFileUrl(null)}
        fileUrl={selectedFileUrl}
      />
    </div>
  );
}

export default ChatCard;
